import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import {
  BookingServicesItems,
  BookingServicesItemsResponse,
} from "@/models/api/administration/bookingService/BookingServicesItems";
import {
  getBookingServiceTable,
  unlinkFromOrder,
  deleteCapture,
  unCancel,
  unVoid,
  cancelBookings,
  getBookingToEdit,
  getIntegrationListToExport,
  checkIfExistAndExport,
  toggleIsLocked,
} from "@/requests/administration/administrationBookingService";

import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { BookingServicesItemsEdit } from "@/models/api/administration/bookingService/BookingServicesItemsEdit";
import { ServiceTypeListItem } from "@/models/api/serviceTypes/ServiceTypeListItem";
import { IntegrationListItem } from "@/models/api/administration/integration/integrationListItem";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface AdministrationBookingServiceStateInterface {
  //Bookings
  bookingServiceFilters: null | Array<CriterionBase>;
  bookingCountItems: number | null;
  bookingDataTableOptions: DataOptions;
  bookings: Array<BookingServicesItems>;
  bookingToEdit: BookingServicesItemsEdit | undefined;
  selectedBookingId: null | number;

  //Dialogs
  isEditDialog: boolean;
  isCancellationDialog: boolean;
  isServiceTypeDialog: boolean;
  isExportDialog: boolean;

  //Cancellation
  comment: string;
  date: Date;

  //ServiceTypes
  toCancelList: ServiceTypeListItem[];
  toCancelId: number[];

  //Export
  toExportList: Array<BookingServicesItems>;
  integrationList: Array<IntegrationListItem>;
  selectedIntegration: string | null;
}

export const AdministrationBookingServiceState: AdministrationBookingServiceStateInterface = {
  //Booking table
  bookingServiceFilters: null,
  bookingCountItems: null,
  bookingDataTableOptions: new DefaultDataOptions(),
  bookings: [],
  bookingToEdit: new BookingServicesItemsEdit(),
  selectedBookingId: null,

  //Dialogs
  isEditDialog: false,
  isCancellationDialog: false,
  isServiceTypeDialog: false,
  isExportDialog: false,

  //Cancellation
  comment: "",
  date: new Date(),

  //ServiceTypes
  toCancelList: [],
  toCancelId: [],

  //Export
  toExportList: [],
  integrationList: [],
  selectedIntegration: null,
};

const AdministrationBookingServiceModule = defineModule({
  state: AdministrationBookingServiceState as AdministrationBookingServiceStateInterface,
  namespaced: true as const,
  // modules: { serviceTypes: ServiceTypesModule },
  getters: {},
  mutations: {
    //Bookings
    setBookingServiceFilters(state, param: Array<CriterionBase>) {
      state.bookingServiceFilters = param;
    },
    setBookingDataTableOptions(state, param: DataOptions) {
      state.bookingDataTableOptions = param;
    },
    setBookings(state, param: BookingServicesItemsResponse) {
      state.bookings = param.items;
      state.bookingCountItems = param.totalCount;
    },
    setBookingToEdit(state, param: BookingServicesItems | undefined) {
      state.bookingToEdit = param;
    },
    setSelectedId(state, param: number) {
      state.selectedBookingId = param;
    },

    //Dialogs
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setIsCancellationDialog(state, param: boolean) {
      state.isCancellationDialog = param;
    },
    setIsServiceTypesDialog(state, param: boolean) {
      state.isServiceTypeDialog = param;
    },
    setIsExportDialog(state, param: boolean) {
      state.isExportDialog = param;
    },

    //Cancellation
    setComment(state, param: string) {
      state.comment = param;
    },
    clearListToCancel(state) {
      state.toCancelList = [];
      state.toCancelId = [];
    },
    setDate(state, param: Date) {
      state.date = param;
    },

    //ServiceType
    setSelectedServiceType(state, param: Array<ServiceTypeListItem>) {
      if (param != null) {
        state.toCancelList = param;
        state.toCancelId = [];
        for (const serviceType of state.toCancelList) {
          state.toCancelId.push(serviceType.id);
        }
      }
    },

    //Export
    clearListToExport(state) {
      state.toExportList = [];
    },
    setIntegrationList(state, param: Array<IntegrationListItem>) {
      state.integrationList = param;
    },
  },

  actions: {
    //Bookings
    async downloadBookings(context) {
      const { commit, state } = _moduleActionContext(context);
      const bookingService = await getBookingServiceTable({
        count: state.bookingDataTableOptions.itemsPerPage,
        page: state.bookingDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.bookingServiceFilters),
        filterName: FILTER_NAMES.BookingServiceStandardFilter,
      });
      const arrayToExport = state.toExportList.map((x) => x.id);
      for (const item of bookingService.items) {
        if (arrayToExport.includes(item.id)) {
          item.toExport = true;
        }
      }
      commit.setBookings(bookingService);
    },
    async editBooking(context, id: number) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedId(id);
      const booking = await getBookingToEdit(id);
      commit.setBookingToEdit(booking);
      commit.setIsEditDialog(true);
    },
    async unlinkFromOrder(context, bookingId: number) {
      const { dispatch } = _moduleActionContext(context);
      await unlinkFromOrder(bookingId);
      await dispatch.downloadBookings();
      await dispatch.editBooking(bookingId);
    },
    async unCancel(context, bookingId: number) {
      const { dispatch } = _moduleActionContext(context);
      await unCancel(bookingId);
      await dispatch.downloadBookings();
      await dispatch.editBooking(bookingId);
    },
    async toggleIsLocked(context, bookingItemId: number) {
      const { dispatch } = _moduleActionContext(context);
      await toggleIsLocked(bookingItemId);
      await dispatch.downloadBookings();
      await dispatch.editBooking(bookingItemId);
    },
    async unVoid(context, bookingId: number) {
      const { dispatch } = _moduleActionContext(context);
      await unVoid(bookingId);
      await dispatch.downloadBookings();
      await dispatch.editBooking(bookingId);
    },
    async deleteCapture(context, bookingId: number) {
      const { dispatch } = _moduleActionContext(context);
      await deleteCapture(bookingId);
      await dispatch.downloadBookings();
      await dispatch.editBooking(bookingId);
    },
    async cancelBookings(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      await cancelBookings(state.toCancelId, state.date, state.comment);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },

    //Export
    //Set list to export, if item exist delete
    async setToExportList(context, param: BookingServicesItems) {
      const { state } = _moduleActionContext(context);
      const index = state.toExportList.findIndex((v) => v.id === param.id);
      if (index == -1) {
        state.toExportList.push(param);
      } else {
        state.toExportList.splice(index, 1);
      }
    },
    async clearListToExport(context) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.clearListToExport();
      await dispatch.downloadBookings();
    },
    async downloadIntegrationList(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getIntegrationListToExport();
      commit.setIntegrationList(response);
    },
    async checkIfExistAndExport(context) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedIntegration && state.toExportList.length > 0) {
        const response = await checkIfExistAndExport(
          state.selectedIntegration,
          false,
          state.toExportList.map((x) => x.id)
        );
        if (response) {
          const model = ConfirmModel.confirmBoolFromContent(
            filterCapitalize(i18n.t("bookingsExistInOtherOffice")) + response.join(", ")
          );
          if (await rootDispatch.popup.addConfirm(model)) {
            await checkIfExistAndExport(
              state.selectedIntegration,
              true,
              state.toExportList.map((x) => x.id)
            );
            await rootDispatch.popup.addSavedOkSnackbar();
          }
        }
        dispatch.clearListToExport();
        commit.setIsExportDialog(false);
      }
    },

    //Cancellation
    async bookingCancellationDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsCancellationDialog(true);
    },
  },
});

export default AdministrationBookingServiceModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationBookingServiceModule);
